import { CHANGE_STATE_REQ_EPP } from "@/apollo/mutations/epp";
import { LIST_ESTADOS_EPP, LIST_SOLICITUDES_EPP } from "@/apollo/queries";
import { GET_BODEGAS_BY_PRODUCTO } from "@/apollo/queries/epp";
import { exMut, exQue, MUTACION_CON_CACHE } from "@/utils/gql";
import { fechaConHora } from "@/utils/time";

export const normalizeStateName = (value = "") =>
  value
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

// Nombre (normalizado) del estado destino por acción y estado actual.
// Se busca por nombre en listEstadosFormulario, así que dependen de cómo
// se llamen los estados en el formulario de la empresa.
const CRITERIOS_ESTADO_DESTINO = {
  aceptar: { 1: ["en proceso"] },
  entrega: {
    2: ["en proceso de entrega"],
  },
  rechazar: { 1: ["rechazada"], 2: ["rechazada"] },
};

const getEstadoObjetivoId = (
  estados = [],
  accion = "",
  estadoActual = null,
) => {
  const porEstado = CRITERIOS_ESTADO_DESTINO[accion];
  if (!porEstado) return null;

  const candidatos = porEstado[estadoActual] || [];
  if (!candidatos.length) return null;

  for (const candidato of candidatos) {
    const encontrado = estados.find(
      (estado) => normalizeStateName(estado?.nombre) === candidato,
    );
    if (encontrado?.id_estado) {
      return Number(encontrado.id_estado);
    }
  }

  return null;
};

const toSafeInt = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const sanitizeDate = (value) => {
  if (!value) return null;
  return String(value).slice(0, 10);
};

const sanitizeProductosDetalle = (productosDetalle = []) => {
  if (!Array.isArray(productosDetalle)) return [];

  return productosDetalle
    .map((producto) => {
      const idProducto = toSafeInt(producto?.id_producto);
      const codigoProducto =
        toSafeInt(producto?.codigo_producto) ||
        toSafeInt(producto?.id_producto);
      const cantidadSolicitada = toSafeInt(producto?.cantidad_solicitada);

      if (!idProducto || !codigoProducto || cantidadSolicitada === null) {
        return null;
      }

      const lotesEntrega = Array.isArray(producto?.lotes_entrega)
        ? producto.lotes_entrega
            .map((lote) => {
              const idLote = toSafeInt(lote?.id_lote);
              const cantidadEntregar = toSafeInt(lote?.cantidad_entregar);

              if (
                !idLote ||
                cantidadEntregar === null ||
                cantidadEntregar <= 0
              ) {
                return null;
              }

              return {
                id_lote: idLote,
                cantidad_entregar: cantidadEntregar,
              };
            })
            .filter(Boolean)
        : [];

      return {
        id_producto: idProducto,
        codigo_producto: codigoProducto,
        cantidad_solicitada: Math.max(cantidadSolicitada, 0),
        fecha_usoexp: sanitizeDate(producto?.fecha_usoexp),
        lotes_entrega: lotesEntrega,
      };
    })
    .filter(Boolean);
};

const buildProductosDetalle = (solicitud) => {
  const respuestas = Array.isArray(solicitud?.respuestas)
    ? solicitud.respuestas
    : [];

  return respuestas
    .map((respuesta) => {
      const idProducto = toSafeInt(respuesta?.producto?.id_producto);
      const codigoProducto =
        toSafeInt(respuesta?.producto?.codigo_producto) || idProducto;
      const cantidadSolicitada = toSafeInt(respuesta?.cantidad_solicitada);

      if (!idProducto || !codigoProducto || cantidadSolicitada === null) {
        return null;
      }

      const lotesEntrega = Array.isArray(respuesta?.lotes)
        ? respuesta.lotes
            .map((lote) => {
              const idLote =
                toSafeInt(lote?.id_lote_fk) ||
                toSafeInt(lote?.lote?.id) ||
                toSafeInt(lote?.id_lote);
              const cantidadEntregar = toSafeInt(lote?.cantidad_entregar);

              if (
                !idLote ||
                cantidadEntregar === null ||
                cantidadEntregar <= 0
              ) {
                return null;
              }

              return {
                id_lote: idLote,
                cantidad_entregar: cantidadEntregar,
              };
            })
            .filter(Boolean)
        : [];

      return {
        id_producto: idProducto,
        codigo_producto: codigoProducto,
        cantidad_solicitada: Math.max(cantidadSolicitada, 0),
        fecha_usoexp: sanitizeDate(respuesta?.fecha_usoexp),
        lotes_entrega: lotesEntrega,
      };
    })
    .filter(Boolean);
};

export const soliState = {
  listSolicitudes: [],
  listEstadosSolicitud: [],
  // Motivo del último cambio de estado fallido antes de llamar al backend
  errorCambioEstado: "",
};

export const soliMutations = {
  CHANGE_ESTADO_SOLICITUD(state, response) {
    if (!state.listSolicitudes?.data?.length) return;

    const found_index = state.listSolicitudes.data.findIndex(
      (datos) => datos.id_solicitud == response.id_solicitud,
    );

    if (found_index === -1) return;

    const currentSolicitud = state.listSolicitudes.data[found_index];
    state.listSolicitudes.data.splice(found_index, 1, {
      ...currentSolicitud,
      ...response,
    });
  },
  SET_LIST_SOLICITUDES(state, response) {
    state.listSolicitudes = response;
  },
  SET_ESTADOS_SOLICITUD(state, response) {
    state.listEstadosSolicitud = response || [];
  },
  SET_ERROR_CAMBIO_ESTADO(state, mensaje) {
    state.errorCambioEstado = mensaje || "";
  },
};

export const soliActions = {
  async getBodegasByProd(_, idProducto) {
    return await exQue(GET_BODEGAS_BY_PRODUCTO, { idProducto }, true);
  },

  async getListSolicitudes({ commit, state, dispatch }, data_) {
    let response = null;
    try {
      await dispatch("ensureFormEpp");

      const variables = {
        ...data_,
        // El backend compara contra la fecha a las 00:00: sin la hora, el
        // último día del rango queda fuera
        fechaFinal: data_?.fechaFinal
          ? fechaConHora(data_.fechaFinal, "23:59:59")
          : null,
        id_formulario: state.formEpp?.id_formulario,
      };

      const res = await exQue(LIST_SOLICITUDES_EPP, variables, true);
      if (res) response = res;
      commit("SET_LIST_SOLICITUDES", response);
    } catch (e) {
      console.error(e);
    }
    return response;
  },

  async getEstadosSolicitudEpp({ commit, state, dispatch }) {
    let response = [];
    try {
      await dispatch("ensureFormEpp");

      const res = await exQue(
        LIST_ESTADOS_EPP,
        { id_formulario: state.formEpp?.id_formulario },
        true,
      );
      if (Array.isArray(res)) response = res;
      commit("SET_ESTADOS_SOLICITUD", response);
    } catch (e) {
      console.error(e);
      commit("SET_ESTADOS_SOLICITUD", []);
    }

    return response;
  },

  async changeEstadoSolicitudEpp(
    { state, dispatch, commit },
    { solicitud, accion, id_usuario, productos_detalle },
  ) {
    commit("SET_ERROR_CAMBIO_ESTADO", "");
    if (!solicitud?.id_solicitud || !solicitud?.estado_solicitud?.id_estado) {
      return null;
    }

    const estadoActual = Number(solicitud.estado_solicitud.id_estado);
    const transicionesValidas = {
      aceptar: [1],
      entrega: [2],
      rechazar: [1, 2],
    };
    const estadosPermitidos = transicionesValidas[accion];
    if (estadosPermitidos && !estadosPermitidos.includes(estadoActual)) {
      console.error(
        `La acción "${accion}" requiere uno de los estados [${estadosPermitidos}], pero el estado actual es ${estadoActual}`,
      );
      return null;
    }

    let estados = state.listEstadosSolicitud;
    if (!Array.isArray(estados) || !estados.length) {
      estados = await dispatch("getEstadosSolicitudEpp");
    }

    const id_nuevo_estado = getEstadoObjetivoId(estados, accion, estadoActual);

    if (!id_nuevo_estado) {
      console.error(`No se encontró estado destino para acción: ${accion}`);
      const esperado = CRITERIOS_ESTADO_DESTINO[accion]?.[estadoActual]?.[0];
      if (esperado) {
        const nombre = esperado.charAt(0).toUpperCase() + esperado.slice(1);
        commit(
          "SET_ERROR_CAMBIO_ESTADO",
          `No existe el estado "${nombre}" en el formulario de EPP. Revisa los estados configurados.`,
        );
      }
      return null;
    }

    const productosDetalle = Array.isArray(productos_detalle)
      ? sanitizeProductosDetalle(productos_detalle)
      : buildProductosDetalle(solicitud);

    const variables = {
      idEstadoActual: estadoActual,
      idNuevoEstado: id_nuevo_estado,
      idSolicitud: Number(solicitud.id_solicitud),
      idSolicitante: Number(id_usuario),
      productosDetalle,
    };

    const response = await exMut(CHANGE_STATE_REQ_EPP, variables, "", "", MUTACION_CON_CACHE);
    return response;
  },
};
